import {
  formatAccessMapForBackend,
  parseAccessMapFromBackend,
} from "./accessMapUtils";
import {
  PUBLIC_ACCESS_OPTIONS,
  publicAccessPolicyToUiOption,
  uiOptionToPublicAccessPolicy,
} from "./accessMapConstants";
import {
  normalizeDescriptionForBackend,
  normalizeDescriptionFromBackend,
} from "../shared/utils/descriptionDisplayUtils";

/**
 * itemTransformers.js
 *
 * Utility functions for transforming items (projects and collections) between
 * frontend format (name, description) and backend format (title, desc).
 *
 * These are pure functions with no side effects - they only transform data structures.
 */

/**
 * Transforms an item from frontend format to backend format
 * 
 * @param {Object} item - The item in frontend format (has name, description)
 * @param {string} itemType - "project" or "collection"
 * @returns {Object} The item in backend format (has title, desc)
 */
export const transformItemToBackend = (item, itemType) => {
  if (!item) return null;

  const backendItem = {
    ...item,
    // Transform field names
    title: item.name || item.title || '',
    desc: normalizeDescriptionForBackend(item.description || item.desc || ''),
  };

  // Remove frontend-specific field names if they exist
  delete backendItem.name;
  delete backendItem.description;

  // Remove frontend-only fields that shouldn't be sent to backend
  delete backendItem.ownerIds;
  delete backendItem.membersIds;
  delete backendItem.viewerIds;
  delete backendItem.updateLog;
  delete backendItem.createdAt;
  delete backendItem.lastUpdated;
  delete backendItem.creatorUser;
  // active view is per-user and handled via collPrefs on backend
  delete backendItem.currentUserActiveViewId;
  delete backendItem.activeViewId;

  // Format accessMap for backend: userRoles + publicAccessPolicy
  const formattedAccessMap = formatAccessMapForBackend(item.accessMap || {});

  const buildAccessMapPayload = (formatted) => ({
    userRoles: (formatted?.userRoles || []).filter((ur) => ur.userId && ur.roletype),
    publicAccessPolicy: Array.isArray(formatted?.publicAccessPolicy)
      ? formatted.publicAccessPolicy
      : uiOptionToPublicAccessPolicy(PUBLIC_ACCESS_OPTIONS.PROTECTED),
  });

  if (formattedAccessMap && Array.isArray(formattedAccessMap.userRoles) && formattedAccessMap.userRoles.length > 0) {
    backendItem.accessMap = buildAccessMapPayload(formattedAccessMap);
    if (backendItem.accessMap.userRoles.length === 0 && item.accessMap?.userRoles?.length) {
      const validRoles = (item.accessMap.userRoles || []).filter(ur => ur && ur.userId && ur.roletype);
      if (validRoles.length > 0) backendItem.accessMap.userRoles = validRoles;
    }
  } else {
    backendItem.accessMap = buildAccessMapPayload(formattedAccessMap);
  }

  // Handle item-specific transformations
  if (itemType === 'project') {
    // Projects: preserve orgDetails structure
    if (!backendItem.orgDetails && item.orgDetails) {
      backendItem.orgDetails = {
        orgName: item.orgDetails.orgName || "",
        groupName: item.orgDetails.groupName || "",
        dept: item.orgDetails.dept || "",
        orgtype: item.orgDetails.orgtype || "PERSONAL"
      };
    }
    // Backend sets creationDate/lastUpdate; do not send projectCreationDate or projectLastUpdate
    delete backendItem.projectCreationDate;
    delete backendItem.projectLastUpdate;

    if (!backendItem.accessMap?.userRoles?.length && item.accessMap?.userRoles?.length) {
      const formatted = formatAccessMapForBackend(item.accessMap);
      backendItem.accessMap = {
        userRoles: formatted.userRoles?.filter(ur => ur.userId && ur.roletype) || [],
        publicAccessPolicy: formatted.publicAccessPolicy?.length
          ? formatted.publicAccessPolicy
          : uiOptionToPublicAccessPolicy(PUBLIC_ACCESS_OPTIONS.PROTECTED),
      };
    }
    if (
      backendItem.accessMap &&
      publicAccessPolicyToUiOption(backendItem.accessMap.publicAccessPolicy) ===
        PUBLIC_ACCESS_OPTIONS.DEFAULT
    ) {
      backendItem.accessMap = {
        ...backendItem.accessMap,
        publicAccessPolicy: uiOptionToPublicAccessPolicy(PUBLIC_ACCESS_OPTIONS.PROTECTED),
      };
    }
  } else if (itemType === 'collection') {
    if (item.updateLog && Array.isArray(item.updateLog)) {
      backendItem.updateInfo = item.updateLog;
    }
    delete backendItem.collDataCreationDate;
    delete backendItem.collDataLastUpdate;
    // Read-only copy of the project's public access. Not stored on the collection.
    delete backendItem.projectPublicAccessPolicy;
  }

  // Pass through custom data fields if provided
  if (Array.isArray(item.customData)) {
    backendItem.customData = item.customData;
  }

  return backendItem;
};

/**
 * Transforms an item from backend format to frontend format
 * 
 * @param {Object} item - The item in backend format (has title, desc)
 * @param {string} itemType - "project" or "collection"
 * @returns {Object} The item in frontend format (has name, description)
 */
export const transformItemFromBackend = (item, itemType) => {
  if (!item) return null;

  const frontendItem = {
    ...item,
    // Transform field names
    name: item.title || item.name || (itemType === 'project' ? 'Untitled Project' : 'Untitled Collection'),
    description: normalizeDescriptionFromBackend(item.desc || item.description),
  };

  // Remove backend-specific field names if they exist
  // Keep them as fallbacks for now to handle edge cases
  // delete frontendItem.title;
  // delete frontendItem.desc;

  // Handle date fields (backend Record uses creationDate and lastUpdate)
  if (itemType === 'project') {
    frontendItem.createdAt = item.creationDate || null;
    frontendItem.lastUpdated = item.lastUpdate || frontendItem.createdAt;
    frontendItem.url = item.publicURL || item.URL || item.url || "";
  } else if (itemType === 'collection') {
    frontendItem.createdAt = item.creationDate || null;
    frontendItem.lastUpdated = item.lastUpdate || frontendItem.createdAt;
    frontendItem.url = item.publicURL || item.url || "";
    // Surface collection-level default view from backend if present
    frontendItem.defaultViewId = item.defaultViewId || null;
  }

  // Map updateInfo to updateLog for frontend compatibility
  frontendItem.updateLog = item.updateInfo || item.updateLog || [];

  // Ensure customData is always an array on the frontend
  if (Array.isArray(item.customData)) {
    frontendItem.customData = item.customData;
  } else {
    frontendItem.customData = [];
  }

  const normalizedAccessMap = parseAccessMapFromBackend(item.accessMap);
  const userRolesArray = normalizedAccessMap.userRoles || [];
  // Prefer username for keys/ids when backend sends it (display uses username)
  const userRoleMap = userRolesArray.reduce((acc, ur) => {
    const key = ur.username || ur.userId;
    if (key) acc[key] = ur.roletype;
    return acc;
  }, {});

  const displayId = (ur) => ur.username || ur.userId;
  frontendItem.ownerIds = userRolesArray
    .filter((ur) => ur.roletype === "OWNER")
    .map(displayId);
  frontendItem.membersIds = userRolesArray
    .filter((ur) => ur.roletype === "CONTRIBUTOR")
    .map(displayId);
  frontendItem.viewerIds = userRolesArray
    .filter((ur) => ur.roletype === "VIEWER")
    .map(displayId);

  frontendItem.accessMap = normalizedAccessMap;
  frontendItem.accessMap.userRoleMap = {
    ...normalizedAccessMap.userRoleMap,
    ...userRoleMap,
  };

  return frontendItem;
};

/**
 * Cleans frontend-only fields from an item before sending to backend
 * This is a more aggressive version that removes ALL frontend-only fields
 * 
 * @param {Object} item - The item to clean
 * @returns {Object} The cleaned item
 */
export const cleanFrontendFields = (item) => {
  if (!item) return null;

  const cleaned = { ...item };

  // Remove frontend-only fields
  delete cleaned.ownerIds;
  delete cleaned.membersIds;
  delete cleaned.viewerIds;
  delete cleaned.updateLog;
  delete cleaned.createdAt;
  delete cleaned.lastUpdated;

  // Remove frontend field names if backend equivalents exist
  if (cleaned.title && cleaned.name) {
    delete cleaned.name;
  }
  if (cleaned.desc && cleaned.description) {
    delete cleaned.description;
  }

  return cleaned;
};

